import { apiSlice } from '../../../store/apiSlice';
import { Empresa, CreateEmpresaPayload, UpdateEmpresaPayload } from '../types';
import { Logger } from '../../../lib/utils';

// Define tipos adicionales
interface EmpresasQueryParams {
  limit?: number;
  offset?: number;
  search?: string;
}

// Define tipos de respuestas para mayor seguridad
interface EmpresasResponse {
  empresas: Empresa[];
  total: number;
  limit: number;
  offset: number;
}

// Manejador de errores genérico para endpoints de empresas
const handleApiError = (error: unknown, endpoint: string) => {
  Logger.error(`Error en ${endpoint}:`, error);
  return error;
};

export const empresasApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Obtener todas las empresas con paginación
    getEmpresas: builder.query<EmpresasResponse, EmpresasQueryParams | void>({
      query: (params) => {
        const { limit = 10, offset = 0, search = '' } = params || {};
        return {
          url: '/empresas',
          params: { limit, offset, search },
        };
      },
      transformResponse: (response: EmpresasResponse) => {
        Logger.api('Respuesta de getEmpresas:', response);
        return response;
      },
      transformErrorResponse: (error) => handleApiError(error, 'getEmpresas'),
      providesTags: (result) =>
        result
          ? [
              ...result.empresas.map(({ id }) => ({ type: 'Empresa' as const, id })),
              { type: 'Empresa' as const, id: 'LIST' },
            ]
          : [{ type: 'Empresa' as const, id: 'LIST' }],
    }),
    
    // Obtener una empresa por su ID
    getEmpresaById: builder.query<Empresa, string | number>({
      query: (id) => `/empresas/${id}`,
      transformErrorResponse: (error) => handleApiError(error, 'getEmpresaById'),
      providesTags: (result, error, id) => [{ type: 'Empresa' as const, id }],
    }),
    
    // Crear una nueva empresa
    createEmpresa: builder.mutation<Empresa, CreateEmpresaPayload>({
      query: (empresa) => ({
        url: '/empresas',
        method: 'POST',
        body: empresa,
      }),
      transformErrorResponse: (error) => handleApiError(error, 'createEmpresa'),
      invalidatesTags: [{ type: 'Empresa' as const, id: 'LIST' }],
    }),
    
    // Actualizar una empresa existente
    updateEmpresa: builder.mutation<Empresa, { id: string | number; changes: UpdateEmpresaPayload }>({
      query: ({ id, changes }) => ({
        url: `/empresas/${id}`,
        method: 'PUT',
        body: changes,
      }),
      transformErrorResponse: (error) => handleApiError(error, 'updateEmpresa'),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Empresa' as const, id: 'LIST' },
        { type: 'Empresa' as const, id },
        ...(result ? [{ type: 'Empresa' as const, id: result.id }] : [])
      ],
    }),
    
    // Eliminar una empresa
    deleteEmpresa: builder.mutation<{ message: string }, string | number>({
      query: (id) => ({
        url: `/empresas/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => handleApiError(error, 'deleteEmpresa'),
      invalidatesTags: [{ type: 'Empresa' as const, id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmpresasQuery,
  useGetEmpresaByIdQuery,
  useCreateEmpresaMutation,
  useUpdateEmpresaMutation,
  useDeleteEmpresaMutation,
} = empresasApiSlice; 